import { Radio, RadioGroup } from '@arthur/ui'
import { Option } from '@common/types/global.types'
import { GREY_COLORS } from '@config/theme/colors'
import { styled } from '@mui/material'
import { FieldValues, useController, UseControllerProps } from 'react-hook-form'

interface RadioInputProps<T extends FieldValues> extends UseControllerProps<T> {
	isRequired?: boolean
	label?: string
	options: Option[]
}

/**
 * Radio input
 * @param control
 * @param defaultValue
 * @param isRequired
 * @param label
 * @param name
 * @param options
 * @param rules
 * @constructor
 */
const RadioInput = <T extends FieldValues>(
	{
		control,
		isRequired,
		label,
		name,
		options,
		rules
	}: RadioInputProps<T>
) => {
	const { field } = useController({
		control,
		name,
		rules: {
			...rules,
			required: isRequired
		}
	})

	return (
		<RadioGroup
			label={label}
			name="company"
		>
			{options.map(option => (
				<Radio
					key={option.value}
					checked={option.value === field.value}
					label={
						option.secondaryLabel
							// @arthur/ui types label as a string but renders it as a React child, which lets us grey out the secondary part
							? (
								<LabelContent>
									{option.label}
									<SecondaryLabel>{option.secondaryLabel}</SecondaryLabel>
								</LabelContent>
							) as unknown as string
							: option.label
					}
					onChange={() => field.onChange(option.value)}
					value={option.value}
				/>
			))}
		</RadioGroup>
	)
}

// .arthur-radio-label is a flex container : a single child keeps the label and its complement in one text flow
const LabelContent = styled('span')({
	minWidth: 0
})

const SecondaryLabel = styled('span')({
	color: GREY_COLORS[500]
})

export default RadioInput
